import { mapState } from 'vuex';
import CustomerService from '@/services/CustomerService.js';

export default {
    computed: {
        ...mapState('checkout', [
            'shippingAddress',
            'invoiceAddress',
            'sameAsDelivery',
            'guestInfo',
        ]),
        guestUser() {
            return this.$store.state.user.guest.guestUser;
        },
        isLoggedIn() {
            return this.$auth.loggedIn || this.guestUser;
        },
    },
    data() {
        return {
            error: null,
            submitLoading: false,
            customerService: new CustomerService({
                http: this.$http,
                auth: this.$auth,
                store: this.$store,
            }),
        };
    },
    methods: {
        async ensureAddressesSaved() {
            // Fast path: user is already logged in (or registered guest) with addresses — nothing to do
            if (this.isLoggedIn && this.addresses?.length > 0) {
                return true;
            }

            this.submitLoading = true;
            this.error = null;
            let addressesChanged = false;

            try {
                // 1. If not logged in and not a guest yet, register as guest
                if (!this.isLoggedIn) {
                    const res = await this.customerService.registerGuest({
                        account: {
                            email: this.guestInfo.email,
                            firstname: this.shippingAddress.Firstname,
                            lastname: this.shippingAddress.Lastname,
                            addressInfo: this.shippingAddress,
                        },
                        languageIsoCode: this.$i18n.locale,
                        currencyIsoCode: this.cart?.Currency?.IsoCode,
                    });

                    // registerGuest creates account + 1 address (delivery and invoice)
                    // If not same, we need to add the second address
                    if (!this.sameAsDelivery) {
                        await this.addressService.add({
                            ...this.invoiceAddress,
                            IsDelivery: false,
                            IsInvoice: true,
                        });
                    }

                    // Clear local storage after successful registration
                    localStorage.removeItem('checkout_guest_data');
                    addressesChanged = true;
                } else if (this.addresses?.length === 0) {
                    // 2. If logged in (or guest already created) but has no addresses in database
                    let idDelivery, idInvoice;

                    if (this.sameAsDelivery) {
                        const saved = await this.addressService.add({
                            ...this.shippingAddress,
                            IsDelivery: true,
                            IsInvoice: true,
                        });
                        idDelivery = saved.IdAddress;
                        idInvoice = saved.IdAddress;
                    } else {
                        const savedDelivery = await this.addressService.add({
                            ...this.shippingAddress,
                            IsDelivery: true,
                            IsInvoice: false,
                        });
                        idDelivery = savedDelivery.IdAddress;

                        const savedInvoice = await this.addressService.add({
                            ...this.invoiceAddress,
                            IsDelivery: false,
                            IsInvoice: true,
                        });
                        idInvoice = savedInvoice.IdAddress;
                    }

                    // Assign addresses to cart
                    await this.addressService.updateAddressType({
                        idAddress: idDelivery,
                        isDelivery: true,
                        isInvoice: idDelivery === idInvoice,
                    });

                    if (idDelivery !== idInvoice) {
                        await this.addressService.updateAddressType({
                            idAddress: idInvoice,
                            isDelivery: false,
                            isInvoice: true,
                        });
                    }
                    addressesChanged = true;
                }

                // Only refresh addresses if we actually created or modified them
                if (addressesChanged) {
                    await this.addressService.fetchAddresses();
                }
                return true;
            } catch (e) {
                console.error('Error in ensureAddressesSaved:', e);
                this.error = this.$t('Error during checkout information processing.');
                return false;
            } finally {
                this.submitLoading = false;
            }
        },

        checkFormOk() {
            const checkoutCarrier = this.$store.state.checkout.checkoutCarrier || this.$store.state.cart?.Shipping?.Carrier;
            const checkoutRelayPoint = this.$store.state.checkout.checkoutRelayPoint || this.$store.state.cart?.Shipping?.Carrier?.RelayPoint;
            const invoiceAddress = this.invoiceAddress;
            const shippingAddress = this.shippingAddress;

            this.clearErrors();
            this.$root.$emit('checkout:validate');

            // 1. Guest email check
            if (!this.isLoggedIn) {
                const email = this.guestInfo?.email;
                const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!email || !re.test(email)) {
                    this.setError(
                        this.$t('Please enter a valid email address.'),
                        '.checkoutGuestEmail'
                    );
                    return false;
                }
            }

            // 2. Shipping address required fields
            const requiredShippingFields = [
                { key: 'Firstname', label: this.$t('First name') },
                { key: 'Lastname', label: this.$t('Last name') },
                { key: 'Address1', label: this.$t('Address') },
                { key: 'Postcode', label: this.$t('ZIP code') },
                { key: 'City', label: this.$t('City') },
                { key: 'CountryIsoCode', label: this.$t('Country') },
                { key: 'MobilePhone', label: this.$t('Mobile phone') },
            ];

            if (!shippingAddress) {
                this.setError(
                    this.$t('Please complete your delivery information.'),
                    '.checkoutAddressDelivery'
                );
                return false;
            }

            for (const field of requiredShippingFields) {
                if (!shippingAddress[field.key] || !shippingAddress[field.key].toString().trim()) {
                    this.setError(
                        field.label + ' ' + this.$t('is required') + '.',
                        '.checkoutAddressDelivery'
                    );
                    return false;
                }
            }

            // 3. Carrier check
            if (!checkoutCarrier?.IdCarrier) {
                this.setError(
                    this.$t('Please select a delivery option'),
                    '.checkoutCarriers'
                );
                return false;
            }

            // 3b. Relay point check
            if (checkoutCarrier?.CarrierType === 'RelayPoint' && !checkoutRelayPoint?.Id) {
                this.setError(
                    this.$t('Please select a relay point.'),
                    '.checkoutCarriers'
                );
                return false;
            }

            // 4. Invoice address required fields
            const sameAsDelivery = this.sameAsDelivery;
            const effectiveInvoice = sameAsDelivery ? shippingAddress : invoiceAddress;

            const requiredInvoiceFields = [
                { key: 'Firstname', label: this.$t('First name') },
                { key: 'Lastname', label: this.$t('Last name') },
                { key: 'Address1', label: this.$t('Address') },
                { key: 'Postcode', label: this.$t('ZIP code') },
                { key: 'City', label: this.$t('City') },
                { key: 'CountryIsoCode', label: this.$t('Country') },
                { key: 'MobilePhone', label: this.$t('Mobile phone') },
            ];

            if (!effectiveInvoice) {
                this.setError(
                    this.$t('Please complete your billing information.'),
                    '.checkoutAddressInvoice'
                );
                return false;
            }

            if (!sameAsDelivery) {
                for (const field of requiredInvoiceFields) {
                    if (!effectiveInvoice[field.key] || !effectiveInvoice[field.key].toString().trim()) {
                        this.setError(
                            field.label + ' ' + this.$t('is required') + '.',
                            '.checkoutAddressInvoice'
                        );
                        return false;
                    }
                }
            }

            return true;
        },

        setError(msg, selector) {
            this.error = msg;
            this.$store.commit('checkout/setCheckoutError', { data: msg });

            if (selector === '.checkoutGuestEmail') {
                this.$root.$emit('checkout:guest-email-error', msg);
                const el = document.querySelector(selector);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            const el = document.querySelector(selector);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },

        clearErrors() {
            this.error = null;
            this.$store.commit('checkout/setCheckoutError', { data: null });
            this.$root.$emit('checkout:clear-validate');
            [
                '.checkoutCarriers',
                '.checkoutAddressInvoice',
                '.checkoutAddressDelivery',
                '.checkoutGuestEmail',
            ].forEach((sel) => {
                const el = document.querySelector(sel);
                if (el) {
                    el.style.border = '';
                    el.style.padding = '';
                }
            });
        },
    },
};
